/* Sample request modal — lets a portal customer request physical samples for
   products in a portfolio, with per-product quantities and an availability-aware
   date suggestion. Submits via the shared /api/enquiry endpoint (type=sample). */

import { useMemo, useState } from 'react';
import { Ico } from './Icons';
import { AvailabilityBadge } from './ProductPeek';
import { SAMPLE_TIMELINE } from '@/portal/data/product';
import { sendEnquiry, EnquiryError } from '@/services/enquiry';
import type { PortalProduct, SampleAvailability } from '@/portal/api/types';

const MAX_QTY = 3;

/* Lead-time in days per availability tier. */
const LEAD_DAYS: Record<SampleAvailability, number> = {
  ready: 14,
  recent: 21,
  enquire: 0, // TBC — no hard minimum, but flag in UI
};

interface SampleLine {
  product: PortalProduct;
  wanted: boolean;
  qty: number;
}

interface Props {
  portfolioTitle: string;
  portfolioClient?: string | null;
  portfolioLink?: string | null;
  products: PortalProduct[];
  userName?: string | null;
  userCompany?: string | null;
  onClose: () => void;
}

/* Format a Date as "D Mon YYYY", e.g. "20 Jul 2026". */
function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ISO date string for <input type="date"> min attribute. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function SampleEnquiryModal({
  portfolioTitle,
  portfolioClient,
  portfolioLink,
  products,
  userName,
  userCompany,
  onClose,
}: Props) {
  const [lines, setLines] = useState<SampleLine[]>(() =>
    products.map((p) => ({ product: p, wanted: true, qty: 1 })),
  );
  const [neededBy, setNeededBy] = useState('');
  const [notes, setNotes] = useState('');
  const [contactName, setContactName] = useState(userName ?? '');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  /* Derive the most restrictive availability tier across wanted products. */
  const { suggestedMinDate, availHint, hasEnquire } = useMemo(() => {
    const wanted = lines.filter((l) => l.wanted);
    if (wanted.length === 0) return { suggestedMinDate: null, availHint: null, hasEnquire: false };

    const tiers = wanted.map((l) => l.product.availability ?? 'enquire');
    const hasEnq = tiers.includes('enquire');
    const maxDays = Math.max(...tiers.map((t) => LEAD_DAYS[t]));
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + maxDays);

    let hint: string;
    if (hasEnq) {
      hint = 'One or more items may need a longer lead time — we will confirm on receipt.';
    } else {
      hint = `Earliest expected availability: ${SAMPLE_TIMELINE[tiers.includes('recent') ? 'recent' : 'ready'].label} (from ${fmtDate(minDate)})`;
    }
    return { suggestedMinDate: maxDays > 0 ? minDate : null, availHint: hint, hasEnquire: hasEnq };
  }, [lines]);

  /* Warn if the chosen date is before the suggested minimum. */
  const dateTooSoon =
    neededBy && suggestedMinDate ? new Date(neededBy) < suggestedMinDate : false;

  const wantedCount = lines.filter((l) => l.wanted).length;

  const setWanted = (code: string, wanted: boolean) =>
    setLines((prev) => prev.map((l) => (l.product.code === code ? { ...l, wanted } : l)));

  const setQty = (code: string, delta: number) =>
    setLines((prev) =>
      prev.map((l) =>
        l.product.code === code
          ? { ...l, qty: Math.min(MAX_QTY, Math.max(1, l.qty + delta)) }
          : l,
      ),
    );

  const buildMessage = (): string => {
    const header = [`SAMPLE REQUEST`, ''];
    const productLines = lines.map((l) => {
      const avail = l.product.availability
        ? SAMPLE_TIMELINE[l.product.availability].label
        : 'Call to discuss availability';
      if (!l.wanted) return `  ✗  ${l.product.name} (${l.product.code})  —  no sample`;
      return `  ✓  ${l.product.name} (${l.product.code})  —  ${l.qty} sample${l.qty > 1 ? 's' : ''}  [${avail}]`;
    });

    const parts = [
      ...header,
      ...productLines,
      '',
      `Samples requested from: ${wantedCount} of ${lines.length} product${lines.length > 1 ? 's' : ''}`,
    ];

    if (neededBy) {
      parts.push(`Needed by: ${fmtDate(new Date(neededBy + 'T12:00:00'))}`);
      if (dateTooSoon) parts.push('  ⚠ Note: date is earlier than the expected lead time.');
    }

    if (userCompany) parts.push(`Company: ${userCompany}`);
    if (notes.trim()) parts.push('', 'Notes:', notes.trim());

    return parts.join('\n');
  };

  const buildPortfolioContext = (): string => {
    const parts = [portfolioTitle];
    if (portfolioClient) parts.push(`for ${portfolioClient}`);
    if (portfolioLink) parts.push(portfolioLink);
    return parts.join('\n');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName.trim()) return;
    if (!contactEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail)) return;
    if (wantedCount === 0 && !neededBy) return;

    setStatus('sending');
    setErrorMsg('');
    try {
      await sendEnquiry({
        type: 'sample',
        name: contactName.trim(),
        email: contactEmail.trim(),
        message: buildMessage(),
        product: buildPortfolioContext(),
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof EnquiryError ? err.message : 'Something went wrong. Please try again.');
    }
  };

  /* Trap keyboard close */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  if (status === 'sent') {
    return (
      <>
        <div className="pl-drawer-scrim" onClick={onClose} />
        <div className="pl-sample-modal" role="dialog" aria-label="Sample request sent" onKeyDown={onKeyDown}>
          <div className="pl-drawer-head">
            <h3>Request sent</h3>
            <button className="pl-x" onClick={onClose} aria-label="Close"><Ico.x width={20} height={20} /></button>
          </div>
          <div className="pl-sample-success">
            <div className="pl-sample-ok-icon" aria-hidden="true">✓</div>
            <h4>Your sample request has been sent.</h4>
            <p>Our team will be in touch to confirm availability and arrange delivery.</p>
            <button className="pl-btn primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pl-drawer-scrim" onClick={onClose} />
      <div className="pl-sample-modal" role="dialog" aria-label="Sample request" onKeyDown={onKeyDown}>
        <div className="pl-drawer-head">
          <h3>Sample Request — <em>{portfolioTitle}</em></h3>
          <button className="pl-x" onClick={onClose} aria-label="Close"><Ico.x width={20} height={20} /></button>
        </div>

        <form className="pl-sample-body" onSubmit={submit} noValidate>
          {/* Product list */}
          <div className="pl-sample-section-label">
            Products <span className="pl-sample-hint">Tick the items you want to sample</span>
          </div>
          <div className="pl-sample-list">
            {lines.map((l) => (
              <div key={l.product.code} className={'pl-sample-row' + (l.wanted ? '' : ' muted')}>
                <label className="pl-sample-check">
                  <input
                    type="checkbox"
                    checked={l.wanted}
                    onChange={(e) => setWanted(l.product.code, e.target.checked)}
                  />
                </label>
                <div className="pl-sample-info">
                  <div className="pl-sample-name">{l.product.name}</div>
                  <div className="pl-sample-meta">
                    <span className="pl-code">{l.product.code}</span>
                    <AvailabilityBadge availability={l.product.availability} />
                  </div>
                </div>
                {l.wanted && (
                  <div className="pl-sample-qty">
                    <button type="button" className="pl-qty-btn" onClick={() => setQty(l.product.code, -1)} disabled={l.qty <= 1} aria-label="Fewer">−</button>
                    <span className="pl-qty-val">{l.qty}</span>
                    <button type="button" className="pl-qty-btn" onClick={() => setQty(l.product.code, +1)} disabled={l.qty >= MAX_QTY} aria-label="More">+</button>
                    {l.qty >= MAX_QTY && (
                      <span className="pl-sample-maxnote">Max {MAX_QTY} — discuss more in Notes</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Needed by */}
          <div className="pl-sample-section-label" style={{ marginTop: 20 }}>
            Needed by
          </div>
          <input
            type="date"
            className="pl-sample-date"
            value={neededBy}
            min={suggestedMinDate && !hasEnquire ? isoDate(suggestedMinDate) : undefined}
            onChange={(e) => setNeededBy(e.target.value)}
          />
          {availHint && (
            <div className={'pl-sample-avail-hint' + (dateTooSoon ? ' warn' : '')}>
              {dateTooSoon ? '⚠ ' : 'ℹ '}{availHint}
            </div>
          )}

          {/* Notes */}
          <div className="pl-sample-section-label" style={{ marginTop: 20 }}>
            Notes <span className="pl-sample-hint">optional</span>
          </div>
          <textarea
            className="pl-sample-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Packaging preferences, delivery address, anything else…"
          />

          {/* Contact */}
          <div className="pl-sample-section-label" style={{ marginTop: 20 }}>
            Your details
          </div>
          <div className="pl-sample-contacts">
            <input
              className={'pl-sample-input' + (!contactName.trim() && status === 'error' ? ' invalid' : '')}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Name *"
              required
            />
            <input
              className={'pl-sample-input' + (!contactEmail.trim() && status === 'error' ? ' invalid' : '')}
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="Email *"
              required
            />
          </div>

          {status === 'error' && <p className="pl-sample-error">{errorMsg}</p>}

          <div className="pl-sample-footer">
            <button type="button" className="pl-btn ghost" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="pl-btn dark"
              disabled={status === 'sending' || wantedCount === 0}
            >
              {status === 'sending' ? 'Sending…' : `Send request · ${wantedCount} product${wantedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
